import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef
} from "react";
import ReactDOM from "react-dom";

interface ModalOptions {
  title: string;
  content: ReactNode;
  footer?: ReactNode;
}

interface ModalContextValue {
  showModal: (options: ModalOptions) => void;
  hideModal: () => void;
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

export const useModal = (): ModalContextValue => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error("useModal must be used within a ModalProvider");
  return ctx;
};

export const ModalProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [options, setOptions] = useState<ModalOptions | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const showModal = useCallback((opts: ModalOptions) => {
    setOptions(opts);
  }, []);

  const hideModal = useCallback(() => {
    setOptions(null);
  }, []);

  useEffect(() => {
    document.body.style.overflow = options ? "hidden" : "";
  }, [options]);

  return (
    <ModalContext.Provider value={{ showModal, hideModal }}>
      <div ref={containerRef} style={{ position: "relative" }}>
        {children}
        {options && (
          <div className="pi-modal-overlay" onClick={hideModal}>
            <div
              className="pi-modal-content"
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pi-modal-header">
                <h2>{options.title}</h2>
                <button
                  className="pi-modal-close-btn"
                  aria-label="Close"
                  onClick={hideModal}
                >
                  ×
                </button>
              </div>
              <div className="pi-modal-body">{options.content}</div>
              {options.footer && (
                <footer className="pi-modal-footer">{options.footer}</footer>
              )}
            </div>
          </div>
        )}
      </div>
    </ModalContext.Provider>
  );
};
